/**
 * /hospitality/filings — Regulatory filing assembly (P5).
 *
 * Three tabs: FinCEN CTR data packets, Nevada GCB Reg-6 fee schedule, and
 * SAR candidate summary. All data is code-computed from the day's cash
 * transactions and gaming-tax records — nothing is invented.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import { PageHeader, StatCard, Panel, money } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/filings")({
  component: FilingsPage,
});

// ── Types ──────────────────────────────────────────────────────────────────
interface CTRTransaction {
  direction: string;
  amount: string;
  area: string;
  instrument: string;
  occurred_at: string | null;
}
interface CTRPatron {
  patron_ref: string;
  patron_name: string;
  cash_in: string;
  cash_out: string;
  ctr_obligations: string[];
  transaction_count: number;
  transactions: CTRTransaction[];
  filing_note: string;
}
interface CTRResult {
  property: string;
  gaming_date: string | null;
  ctr_count: number;
  ctrs: CTRPatron[];
  instructions: string | null;
  message?: string;
}

interface Reg6BracketRow {
  label: string;
  rate: string;
  base: string;
  fee: string;
}
interface Reg6Result {
  property: string;
  period: string | null;
  as_of_date: string | null;
  gross_gaming_revenue: { slot_win: string; table_win: string; ggr: string } | null;
  fee_schedule: Reg6BracketRow[];
  total_fee: string | null;
  effective_rate: string | null;
  instructions: string | null;
  message?: string;
}

interface SARCandidate {
  patron_ref: string;
  patron_name: string;
  cash_in: string;
  cash_out: string;
  indicators: string[];
  recommendation: string;
}
interface SARResult {
  property: string;
  gaming_date: string | null;
  candidate_count: number;
  candidates: SARCandidate[];
  instructions: string | null;
  message?: string;
}

type Tab = "ctr" | "reg6" | "sar";

function FilingsPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const [tab, setTab] = useState<Tab>("ctr");

  const ctrQ = useQuery({
    queryKey: ["hospitality-filings-ctr"],
    queryFn: () => api<CTRResult>("/api/hospitality/filings/ctr"),
  });
  const reg6Q = useQuery({
    queryKey: ["hospitality-filings-reg6"],
    queryFn: () => api<Reg6Result>("/api/hospitality/filings/reg6"),
    enabled: tab === "reg6",
  });
  const sarQ = useQuery({
    queryKey: ["hospitality-filings-sar"],
    queryFn: () => api<SARResult>("/api/hospitality/filings/sar"),
    enabled: tab === "sar",
  });

  const ctr = ctrQ.data;
  const reg6 = reg6Q.data;
  const sar = sarQ.data;

  const TABS: { key: Tab; label: string }[] = [
    { key: "ctr", label: "FinCEN CTR" },
    { key: "reg6", label: "GCB Reg-6" },
    { key: "sar", label: "SAR Candidates" },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Regulatory Filings"
        subtitle="FinCEN CTR data packets · GCB Reg-6 fee schedule · SAR candidates"
      />

      {/* Tab bar */}
      <div className="flex gap-1 px-6 pt-1 pb-0 border-b border-slate-200 dark:border-slate-800">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
              tab === t.key
                ? "bg-white dark:bg-slate-900 border border-b-white dark:border-slate-700 dark:border-b-slate-900 text-slate-900 dark:text-slate-100 -mb-px"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {/* ── CTR tab ── */}
        {tab === "ctr" && (
          <>
            {ctrQ.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
            {!ctrQ.isLoading && ctr?.message && (
              <div className="text-sm text-slate-500 py-12 text-center">{ctr.message}</div>
            )}
            {!ctrQ.isLoading && ctr && !ctr.message && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                  <StatCard label="Gaming date" value={ctr.gaming_date ?? "—"} />
                  <StatCard label="CTR filings required" value={String(ctr.ctr_count)} />
                  <StatCard label="Threshold" value="$10,000" />
                </div>
                {ctr.instructions && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded p-3">
                    {ctr.instructions}
                  </div>
                )}
                {ctr.ctrs.map((p) => (
                  <Panel key={p.patron_ref} label={`${p.patron_name} (${p.patron_ref})`}>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Cash in</div>
                        <div className="text-sm font-semibold">{money(p.cash_in)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Cash out</div>
                        <div className="text-sm font-semibold">{money(p.cash_out)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Txns</div>
                        <div className="text-sm font-semibold">{p.transaction_count}</div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{p.filing_note}</div>
                    {p.transactions.length > 0 && (
                      <table className="w-full text-xs mt-2">
                        <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase text-slate-400">
                          <tr>
                            <th className="text-left py-1 pr-3">Direction</th>
                            <th className="text-left py-1 px-3">Area</th>
                            <th className="text-left py-1 px-3">Instrument</th>
                            <th className="text-right py-1 pl-3">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.transactions.map((t, i) => (
                            <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                              <td className="py-1 pr-3 capitalize">{t.direction}</td>
                              <td className="py-1 px-3 text-slate-500">{t.area}</td>
                              <td className="py-1 px-3 text-slate-500">{t.instrument}</td>
                              <td className="py-1 pl-3 text-right tabular-nums font-medium">
                                {money(t.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </Panel>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Reg-6 tab ── */}
        {tab === "reg6" && (
          <>
            {reg6Q.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
            {!reg6Q.isLoading && reg6?.message && (
              <div className="text-sm text-slate-500 py-12 text-center">{reg6.message}</div>
            )}
            {!reg6Q.isLoading && reg6 && !reg6.message && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  <StatCard label="Period" value={reg6.period ?? "—"} />
                  <StatCard label="GGR" value={money(reg6.gross_gaming_revenue?.ggr ?? null, 0)} />
                  <StatCard label="Total fee" value={money(reg6.total_fee ?? null, 0)} />
                  <StatCard label="Effective rate" value={reg6.effective_rate ?? "—"} />
                </div>
                {reg6.instructions && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded p-3">
                    {reg6.instructions}
                  </div>
                )}
                {reg6.gross_gaming_revenue && (
                  <Panel label="Gross gaming revenue (MTD)">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Slot win</div>
                        <div className="text-sm font-semibold">
                          {money(reg6.gross_gaming_revenue.slot_win, 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Table win</div>
                        <div className="text-sm font-semibold">
                          {money(reg6.gross_gaming_revenue.table_win, 0)}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">GGR</div>
                        <div className="text-sm font-semibold">
                          {money(reg6.gross_gaming_revenue.ggr, 0)}
                        </div>
                      </div>
                    </div>
                  </Panel>
                )}
                <Panel label="Fee schedule (NRS 463.370)">
                  <table className="w-full text-sm">
                    <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase text-slate-400">
                      <tr>
                        <th className="text-left py-1.5 pr-3">Bracket</th>
                        <th className="text-right py-1.5 px-3">Rate</th>
                        <th className="text-right py-1.5 px-3">GGR base</th>
                        <th className="text-right py-1.5 pl-3">Fee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reg6.fee_schedule.map((b, i) => (
                        <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                          <td className="py-1.5 pr-3 text-xs text-slate-600 dark:text-slate-300">{b.label}</td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-xs">{b.rate}</td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-xs">
                            {money(b.base, 0)}
                          </td>
                          <td className="py-1.5 pl-3 text-right tabular-nums text-xs font-medium">
                            {money(b.fee, 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
              </div>
            )}
          </>
        )}

        {/* ── SAR tab ── */}
        {tab === "sar" && (
          <>
            {sarQ.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
            {!sarQ.isLoading && sar?.message && (
              <div className="text-sm text-slate-500 py-12 text-center">{sar.message}</div>
            )}
            {!sarQ.isLoading && sar && !sar.message && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2.5">
                  <StatCard label="Gaming date" value={sar.gaming_date ?? "—"} />
                  <StatCard label="Candidates" value={String(sar.candidate_count)} />
                </div>
                {sar.instructions && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded p-3">
                    {sar.instructions}
                  </div>
                )}
                {sar.candidates.length === 0 && (
                  <div className="text-sm text-slate-500 py-8 text-center">
                    No SAR candidates for this gaming date.
                  </div>
                )}
                {sar.candidates.map((c) => (
                  <Panel
                    key={c.patron_ref}
                    label={`${c.patron_name} (${c.patron_ref})`}
                  >
                    <div className="grid grid-cols-2 gap-3 mb-2">
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Cash in</div>
                        <div className="text-sm font-semibold">{money(c.cash_in)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 mb-0.5">Cash out</div>
                        <div className="text-sm font-semibold">{money(c.cash_out)}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {c.indicators.map((ind, i) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                        >
                          {ind}
                        </span>
                      ))}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {c.recommendation}
                    </div>
                  </Panel>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
